import { Controller } from '@hotwired/stimulus'
import { renderPdf, watchContainerWidth } from '../lib/pdf_renderer'
import { trackScroll, checkIfFitsWithoutScroll, waitForVisible } from '../lib/scroll_tracker'

export default class extends Controller {
  static targets = ['container', 'loading', 'check', 'pageCount']
  static values = {
    url: String,
    docId: Number,
    overlays: { type: Object, default: { signatures: [], initials: [] } },
    signatureLabel: { type: String, default: 'Votre signature' },
    initialsLabel: { type: String, default: 'Vos initiales' },
    pageSingular: { type: String, default: 'page' },
    pagePlural: { type: String, default: 'pages' },
  }

  connect() {
    this.scrolled = false
    this.rendering = false
    waitForVisible(this.containerTarget, () => this.loadDocument())
  }

  disconnect() {
    if (this.stopWatching) this.stopWatching()
  }

  async loadDocument() {
    try {
      const renderedPages = await renderPdf(this.urlValue, this.containerTarget)
      this.drawOverlays(renderedPages)
      this.updatePageCount(renderedPages.length)
      this.hideLoading()
      trackScroll(this.containerTarget, () => this.markAsRead())
      checkIfFitsWithoutScroll(this.containerTarget, () => this.markAsRead())
      this.stopWatching = watchContainerWidth(
        this.containerTarget,
        () => this.rerender()
      )
    } catch {
      this.containerTarget.innerHTML =
        '<p class="text-center text-base-content/60 p-8">Document unavailable</p>'
      this.hideLoading()
    }
  }

  async rerender() {
    if (this.rendering) return
    this.rendering = true
    const scrollTop = this.containerTarget.scrollTop
    try {
      this.containerTarget.innerHTML = ''
      const renderedPages = await renderPdf(this.urlValue, this.containerTarget)
      this.drawOverlays(renderedPages)
      this.containerTarget.scrollTop = scrollTop
    } catch (error) {
      console.error('PDF re-render failed', error)
    } finally {
      this.rendering = false
    }
  }

  drawOverlays(renderedPages) {
    const overlays = this.overlaysValue || {}
    const signatures = Array.isArray(overlays.signatures) ? overlays.signatures : []
    const initials = Array.isArray(overlays.initials) ? overlays.initials : []
    if (signatures.length === 0 && initials.length === 0) return

    const pagesByNumber = new Map(renderedPages.map((p) => [p.page, p]))

    renderedPages.forEach((page) => {
      const wrapper = document.createElement('div')
      wrapper.style.position = 'relative'
      wrapper.style.display = 'block'
      page.canvas.parentNode.insertBefore(wrapper, page.canvas)
      wrapper.appendChild(page.canvas)
      page.wrapper = wrapper
    })

    signatures
      .filter((o) => pagesByNumber.has(o.page))
      .forEach((overlay) => {
        const page = pagesByNumber.get(overlay.page)
        this.appendOverlay(page, overlay, 'signature')
      })

    initials
      .filter((o) => pagesByNumber.has(o.page))
      .forEach((overlay) => {
        const page = pagesByNumber.get(overlay.page)
        this.appendOverlay(page, overlay, 'initials')
      })
  }

  appendOverlay(page, overlay, kind) {
    const { scale, pdfHeight, wrapper } = page
    const leftPx = overlay.x * scale
    const widthPx = overlay.width * scale
    const heightPx = overlay.height * scale
    const topPx = (pdfHeight - overlay.y - overlay.height) * scale

    const box = document.createElement('div')
    box.style.position = 'absolute'
    box.style.left = `${leftPx}px`
    box.style.top = `${topPx}px`
    box.style.width = `${widthPx}px`
    box.style.height = `${heightPx}px`
    box.style.boxSizing = 'border-box'
    box.style.borderRadius = '6px'
    box.style.display = 'flex'
    box.style.alignItems = 'center'
    box.style.justifyContent = 'center'
    box.style.textAlign = 'center'
    box.style.padding = '4px'
    box.style.pointerEvents = 'none'
    box.style.fontFamily = '"General Sans", system-ui, sans-serif'
    box.style.fontWeight = '600'
    box.style.color = '#023836'
    box.style.userSelect = 'none'

    if (kind === 'signature') {
      box.style.border = '2px dashed #023836'
      box.style.background = 'rgba(177, 250, 51, 0.28)'
      box.textContent = this.signatureLabelValue
    } else {
      box.style.border = '1.5px dashed #023836'
      box.style.background = 'rgba(2, 56, 54, 0.10)'
      box.textContent = this.initialsLabelValue
    }

    const fontSize = Math.max(8, Math.min(13, heightPx / 4))
    box.style.fontSize = `${fontSize}px`
    box.style.lineHeight = '1.15'

    page.wrapper.appendChild(box)
  }

  markAsRead() {
    if (this.scrolled) return
    this.scrolled = true

    if (this.hasCheckTarget) {
      this.checkTarget.classList.remove('hidden')
    }

    this.dispatch('scrolledToBottom', {
      detail: { docId: this.docIdValue }
    })
  }

  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add('hidden')
    }
  }

  updatePageCount(count) {
    if (!this.hasPageCountTarget || !count) return
    const label = count > 1 ? this.pagePluralValue : this.pageSingularValue
    this.pageCountTarget.textContent = ` · ${count} ${label}`
  }
}
